import { NAVBAR_VIEWS } from '@const';
import { Badge, Box, Tooltip, Typography, useTheme } from '@mui/material';
import { NavbarOption } from '@types';
import { ReactNode, useCallback, useState } from 'react';
import NavbarPopover from './TackleboxPopover';

export interface NavbarButtonProps {
  view?: string;
  viewConfig?: NavbarOption;
  onViewChange?: (view: NavbarOption) => void;
  onToggle?: (view: NavbarOption) => void;
  onClick?: () => void;
  activePage?: null | string;
  isActive?: boolean;
  popoverToggleStates?: Record<string, boolean>;
  boldTextOnActive?: boolean;
  backgroundActive?: boolean;
  showBadge?: boolean;
  label?: ReactNode;
  iconTextGap?: number | string;
  renderPopover?: (anchorEl: HTMLElement | null, open: boolean, onClose: () => void) => ReactNode;
}
const NavbarButton = ({
  view,
  viewConfig: propViewConfig,
  onViewChange,
  onToggle,
  onClick,
  activePage,
  isActive,
  popoverToggleStates,
  boldTextOnActive,
  backgroundActive,
  showBadge,
  label,
  iconTextGap = '10px',
  renderPopover
}: NavbarButtonProps) => {
  const theme = useTheme();
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);

  const viewConfig = propViewConfig || (view ? NAVBAR_VIEWS[view] : null);
  const popoverOpen = Boolean(popoverAnchorEl);

  // Always define hooks before any early return
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!viewConfig) return;
      // If direct onClick provided, use it
      if (onClick) {
        onClick();
        return;
      }

      // Check if this button has a popover
      if (viewConfig.hasPopover || renderPopover) {
        setPopoverAnchorEl(event.currentTarget);
        return;
      }

      if (viewConfig.isToggle && onToggle) {
        onToggle(viewConfig);
      } else if (onViewChange) {
        onViewChange(viewConfig);
      }
    },
    [onClick, onViewChange, onToggle, viewConfig, renderPopover]
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverAnchorEl(null);
  }, []);

  const handlePopoverToggle = useCallback(
    (toggleAction: string) => {
      onToggle?.({ toggleAction, hasTab: true } as NavbarOption);
    },
    [onToggle]
  );

  if (!viewConfig) return null;

  // Text styling opt-in: false by default, callers explicitly enable
  const textActive = boldTextOnActive ?? false;

  const ViewIcon = isActive ? viewConfig.activeIcon : viewConfig.inactiveIcon;
  const activeColor = viewConfig.activeColor ?? theme.palette.primary.main;
  const tooltipText = viewConfig.isToggle ? (isActive ? viewConfig.tooltipActive || '' : viewConfig.tooltipInactive || '') : viewConfig.tooltip || '';

  return (
    <>
      <Tooltip title={tooltipText} placement="right">
        <Box
          key={view || 'action-button'}
          onClick={handleClick}
          tabIndex={0}
          role="button"
          sx={{
            display: 'flex',
            padding: theme.spacing(1.5, 1.25),
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: iconTextGap,
            cursor: 'pointer',
            color: textActive && isActive ? activeColor : theme.palette.text.primary,
            fontWeight: textActive && isActive ? 600 : 400,
            height: '64px',
            width: '100%',
            backgroundColor: popoverOpen ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
            '&:hover': {
              color: textActive && isActive ? activeColor : theme.palette.action.active,
              backgroundColor: textActive && isActive ? theme.palette.action.selected : theme.palette.action.hover
            }
          }}
        >
          {backgroundActive ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isActive ? activeColor : 'transparent',
                borderRadius: 1,
                p: 0.625
              }}
            >
              <ViewIcon fontSize="small" sx={{ color: isActive ? 'white' : theme.palette.action.active }} />
            </Box>
          ) : (
            <Badge color="primary" variant="dot" invisible={!showBadge || !isActive} sx={{ '& .MuiBadge-badge': { right: -6 } }}>
              <ViewIcon fontSize="small" sx={{ color: isActive ? activeColor : theme.palette.action.active }} />
            </Badge>
          )}

          <Typography
            variant="listSm"
            sx={{
              fontWeight: textActive && isActive ? 700 : 400,
              color: textActive && isActive ? activeColor : theme.palette.text.secondary
            }}
          >
            {label ?? viewConfig.displayName}
          </Typography>
        </Box>
      </Tooltip>

      {renderPopover
        ? renderPopover(popoverAnchorEl, popoverOpen, handlePopoverClose)
        : viewConfig.hasPopover &&
          viewConfig.popoverOptions && (
            <NavbarPopover
              anchorEl={popoverAnchorEl}
              open={popoverOpen}
              onClose={handlePopoverClose}
              onToggle={handlePopoverToggle}
              options={viewConfig.popoverOptions}
              activePage={activePage}
              toggleStates={popoverToggleStates}
            />
          )}
    </>
  );
};

export default NavbarButton;
